import { Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { NotificationItem } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { cameFrom } from "../lib/returnTo";
import { Avatar, BackBar, EmptyState, Spinner } from "../components/ui";
import { when } from "../components/Discussion";
import { LockIcon, MailIcon, PhoneIcon } from "../components/Icons";

/**
 * Everything that notified you, newest first (PLAN.md §Notifications). Each
 * line says who and where; the message stays behind the gate in Talk, so a
 * reply past where you are shows as sealed here too. Tapping goes to the exact
 * place and marks it read on every device. Nobody else ever sees what you read.
 */
export function InboxPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useInfiniteQuery({
    queryKey: ["notifications"], queryFn: ({ pageParam }) => api.notifications(pageParam),
    initialPageParam: null as string | null, getNextPageParam: (p) => p.next,
  });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["notifications"] }), qc.invalidateQueries({ queryKey: ["notif-count"] })]);
  const read = useMutation({ mutationFn: api.markRead, onSuccess: (r) => { qc.setQueryData(["notif-count"], r); void refresh(); } });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const unread = q.data?.pages[0]?.unread ?? 0;

  const open = (n: NotificationItem) => {
    if (!n.read) read.mutate({ ids: [n.id] });
    if (n.link) void navigate({ href: n.link });
  };

  return (
    <main className="page stack inbox">
      <BackBar label="Your clubs" link={{ to: "/clubs", search: { all: true } }} onClick={(e) => { if (cameFrom("/")) { e.preventDefault(); window.history.back(); } }}
        right={unread > 0 ? <button type="button" className="btn ghost sm" disabled={read.isPending} onClick={() => read.mutate({ all: true })}>Mark all read</button> : undefined} />
      <h1>Inbox</h1>
      {q.isPending && <Spinner />}
      {q.isError && <p className="error">{describeError(q.error)}</p>}
      {q.data && items.length === 0 && (
        <EmptyState title="Nothing yet">Replies, meetings and news from your clubs land here.</EmptyState>
      )}
      {items.length > 0 && (
        <ul className="nt-list">
          {items.map((n) => <li key={n.id}><Row n={n} onOpen={() => open(n)} /></li>)}
        </ul>
      )}
      {q.hasNextPage && <button type="button" className="btn ghost" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>{q.isFetchingNextPage ? "Loading…" : "Older"}</button>}
      <p className="muted small center nt-key">
        <span><PhoneIcon width={13} height={13} />also pushed</span>
        <span><MailIcon width={14} height={14} />also emailed</span>
        <Link to="/me/settings" hash="notifications">Choose where things go</Link></p>
    </main>
  );
}

function Row({ n, onOpen }: { n: NotificationItem; onOpen: () => void }) {
  const face = n.actor ? <Avatar name={n.actor.name} image={n.actor.image} /> : <Avatar name={n.club?.name ?? "Pagemates"} />;
  const meta = (
    <span className="nt-meta">
      {n.club && <span>{n.club.name}</span>}
      <span>{when(n.createdAt)}</span>
      {n.sealedAt !== null && <span className="nt-seal"><LockIcon width={13} height={13} />sealed until {n.sealedAt}%</span>}
      {n.channels.length > 0 && (
        <span className="nt-ch">
          {n.channels.includes("push") && <PhoneIcon width={13} height={13} role="img" aria-hidden={false} aria-label="Also pushed" />}
          {n.channels.includes("email") && <MailIcon width={14} height={14} role="img" aria-hidden={false} aria-label="Also emailed" />}
        </span>
      )}
    </span>
  );
  const inner = <>{face}<span className="nt-body"><span className="nt-text">{n.text}</span>{meta}</span>{!n.read && <i className="nt-dot" aria-label="Unread" />}</>;
  if (!n.link) return <div className={`nt gone${n.read ? "" : " unread"}`} onClick={n.read ? undefined : onOpen}>{inner}</div>;
  return <button type="button" className={`nt${n.read ? "" : " unread"}`} onClick={onOpen}>{inner}</button>;
}
